/**
 * Endpoint constants — only define here when a path is parameterized or used
 * from more than one place. Inline string literals are fine for single-use.
 */

export const ENDPOINTS = {
  health: '/health',
  auth: {
    login: '/auth/login',
    register: '/auth/register',
    logout: '/auth/logout',
    refresh: '/auth/refresh',
    me: '/auth/me',
    changePassword: '/auth/change-password',
    providers: '/auth/providers',
  },
  account: {
    avatar: '/account/me/avatar',
    avatarRaw: '/account/me/avatar/raw',
  },
  me: {
    credits: '/me/credits',
    assistant: '/me/assistant',
    notifications: '/me/notifications',
    notificationsUnread: '/me/notifications/unread-count',
    notificationsReadAll: '/me/notifications/read-all',
    notificationRead: (id: string): string => `/me/notifications/${id}/read`,
  },
  admin: {
    metrics: '/admin/metrics',
    aiUsage: '/admin/ai-usage',
    userCredits: (userId: string): string => `/admin/users/${userId}/credits`,
    userPlan: (userId: string): string => `/admin/users/${userId}/plan`,
  },
  users: {
    root: '/users',
    byId: (id: string): string => `/users/${id}`,
  },
  invitations: {
    root: '/invitations',
    byToken: (token: string): string => `/invitations/${token}`,
    accept: '/invitations/accept',
  },
  roles: {
    root: '/roles',
    byId: (id: string): string => `/roles/${id}`,
  },
  permissions: {
    root: '/permissions',
  },
  systemSettings: {
    root: '/system-settings',
    byKey: (key: string): string => `/system-settings/${key}`,
  },
  plans: {
    root: '/plans',
    byId: (id: string): string => `/plans/${id}`,
  },
  candidates: {
    me: {
      profile: '/candidates/me/profile',
      cv: '/candidates/me/cv',
      cvExtract: '/candidates/me/cv/extract',
      cvAnalyze: '/candidates/me/cv/analyze',
      cvAnalysis: '/candidates/me/cv/analysis',
      images: '/candidates/me/images',
      employabilityScore: '/candidates/me/employability-score',
      privacy: '/candidates/me/privacy',
      skillGap: '/candidates/me/skill-gap',
      jobMatches: '/candidates/me/job-matches',
      recommendation: '/candidates/me/recommendation',
      recommendationRegenerate: '/candidates/me/recommendation/regenerate',
    },
  },
  jobs: {
    root: '/jobs',
    byId: (id: string): string => `/jobs/${id}`,
    apply: (id: string): string => `/jobs/${id}/apply`,
    applications: (id: string): string => `/jobs/${id}/applications`,
  },
  hiring: {
    myApplications: '/me/applications',
    withdrawApplication: (id: string): string => `/me/applications/${id}/withdraw`,
    applicationStatus: (id: string): string => `/applications/${id}/status`,
    companyApplications: '/company/applications',
    companyProfile: '/company/profile',
    companyLogo: '/company/logo',
    companyLogoRaw: '/company/logo/raw',
    companyLogoByOwnerRaw: (ownerUserId: string): string =>
      `/company/${ownerUserId}/logo/raw`,
    companyOffers: '/company/offers',
    withdrawOffer: (id: string): string => `/company/offers/${id}/withdraw`,
    myOffers: '/me/offers',
    respondOffer: (id: string): string => `/me/offers/${id}`,
    makeOffer: (candidateUserId: string): string =>
      `/candidates/${candidateUserId}/offers`,
    candidateSearch: '/candidates/search',
  },
  earlyAccess: {
    requests: '/early-access/requests',
    requestById: (id: string): string => `/early-access/requests/${id}`,
  },
  auditLogs: {
    root: '/audit-logs',
  },
} as const;
